import * as React from 'react';
import { Participant, ParticipantEvent, Track, TrackEvent } from 'livekit-client';

export type ScreenShareAudioBorderOptions = {
  /**
   * Enables/disables the whole monitor.
   * Changing this restarts the monitor.
   */
  enabled?: boolean;

  /**
   * If true, clears the speaking UI without stopping analysis.
   * Useful when local volume is 0 but you do not want to restart the monitor.
   */
  silent?: boolean;

  /**
   * RMS level required to consider screen-share audio active.
   * Lower if quiet system audio does not trigger.
   */
  threshold?: number;

  /**
   * RMS level considered "loud". Used to normalize intensity to 0..1.
   */
  peak?: number;

  /**
   * How fast the level rises when audio becomes louder.
   */
  attack?: number;

  /**
   * How fast the level falls when audio becomes quieter.
   */
  release?: number;

  /**
   * How long to keep the speaking state after last detected audio.
   */
  holdMs?: number;

  /**
   * Class added to the border element while screen-share audio is active.
   */
  speakingClass?: string;
};

type AudioFrameState = {
  level: number;
  intensity: number;
  active: boolean;
};

const DEFAULT_SPEAKING_CLASS = 'screen-share-audio-speaking';

/**
 * Some LiveKit versions expose Track.Source.ScreenShareAudio,
 * some older builds may only use the raw string.
 */
const SCREEN_SHARE_AUDIO_SOURCE: string =
  (Track.Source as any).ScreenShareAudio ?? 'screen_share_audio';

let sharedAudioContext: AudioContext | undefined;

function getSharedAudioContext(): AudioContext | undefined {
  if (typeof window === 'undefined') {
    return undefined;
  }

  const Ctor = window.AudioContext ?? (window as any).webkitAudioContext;

  if (!Ctor) {
    return undefined;
  }

  if (!sharedAudioContext) {
    sharedAudioContext = new Ctor();
  }

  if (sharedAudioContext.state === 'suspended') {
    void sharedAudioContext.resume().catch(() => undefined);
  }

  return sharedAudioContext;
}

function getPublications(participant: Participant): any[] {
  const result: any = participant.getTrackPublications?.();

  if (!result) {
    return [];
  }

  if (Array.isArray(result)) {
    return result;
  }

  if (result instanceof Map) {
    return Array.from(result.values());
  }

  if (typeof result.values === 'function') {
    return Array.from(result.values());
  }

  if (typeof result[Symbol.iterator] === 'function') {
    return Array.from(result);
  }

  return Object.values(result);
}

function isScreenShareAudioPublication(publication: any): boolean {
  if (!publication) {
    return false;
  }

  return publication.source === SCREEN_SHARE_AUDIO_SOURCE && publication.kind === 'audio';
}

function isPublicationSubscribed(publication: any): boolean {
  if (!publication) {
    return false;
  }

  return (
    publication.isSubscribed === true ||
    publication.subscribed === true ||
    publication.subscriptionStatus === 'subscribed'
  );
}

/**
 * Analyzes a participant's screen_share_audio track and applies
 * a speaking state to the given DOM element.
 *
 * The element receives:
 * - class: options.speakingClass
 * - CSS variable: --ss-audio-level = 0..1
 * - data attribute: data-screen-share-audio-speaking = "true" | "false"
 */
export function useScreenShareAudioBorder(
  participant: Participant | undefined | null,
  ref: React.RefObject<HTMLElement | null>,
  options: ScreenShareAudioBorderOptions = {}
) {
  const { enabled = true, speakingClass = DEFAULT_SPEAKING_CLASS } = options;

  const optionsRef = React.useRef(options);

  React.useEffect(() => {
    optionsRef.current = options;
  });

  React.useEffect(() => {
    const element = ref.current;

    if (!element || !participant || !enabled) {
      if (element) {
        element.classList.remove(speakingClass);
        element.style.setProperty('--ss-audio-level', '0');
        element.dataset.screenShareAudioSpeaking = 'false';
      }

      return;
    }

    let currentTrack: any = null;
    let cleanupCurrentTrack: (() => void) | undefined;
    let stopMonitor: (() => void) | undefined;
    let raf = 0;

    let lastActive = false;
    let lastIntensity = -1;

    const clearUi = () => {
      element.classList.remove(speakingClass);
      element.style.setProperty('--ss-audio-level', '0');
      element.dataset.screenShareAudioSpeaking = 'false';

      lastActive = false;
      lastIntensity = -1;
    };

    clearUi();

    const applyFrame = ({ active, intensity }: AudioFrameState) => {
      const opts = optionsRef.current;

      const shouldShow = !opts.silent && active && intensity > 0;

      if (!shouldShow) {
        if (lastActive) {
          clearUi();
        }

        return;
      }

      if (!lastActive) {
        element.classList.add(speakingClass);
        element.dataset.screenShareAudioSpeaking = 'true';
        lastActive = true;
      }

      /**
       * Avoid writing CSS variable every single frame if it barely changed.
       */
      if (Math.abs(intensity - lastIntensity) > 0.015) {
        element.style.setProperty('--ss-audio-level', intensity.toFixed(3));
        lastIntensity = intensity;
      }
    };

    const detach = () => {
      cleanupCurrentTrack?.();
      cleanupCurrentTrack = undefined;

      stopMonitor?.();
      stopMonitor = undefined;

      currentTrack = null;
      clearUi();
    };

    const startMonitor = (mediaStreamTrack: MediaStreamTrack) => {
      stopMonitor?.();
      stopMonitor = undefined;

      if (mediaStreamTrack.readyState !== 'live') {
        return;
      }

      const ctx = getSharedAudioContext();

      if (!ctx) {
        return;
      }

      const stream = new MediaStream([mediaStreamTrack]);
      const source = ctx.createMediaStreamSource(stream);

      const analyser = ctx.createAnalyser();
      analyser.fftSize = 1024;
      analyser.smoothingTimeConstant = 0.3;

      /**
       * Important:
       * Do NOT connect analyser to ctx.destination.
       * We only want to analyze audio, not play it twice.
       */
      source.connect(analyser);

      const data = new Uint8Array(analyser.fftSize);

      let smoothedLevel = 0;
      let lastAboveThresholdAt = 0;

      const loop = () => {
        const opts = optionsRef.current;

        const threshold = opts.threshold ?? 0.018;
        const peak = opts.peak ?? 0.14;
        const attack = opts.attack ?? 0.35;
        const release = opts.release ?? 0.14;
        const holdMs = opts.holdMs ?? 240;

        analyser.getByteTimeDomainData(data);

        let sum = 0;

        for (let i = 0; i < data.length; i += 1) {
          const sample = (data[i] - 128) / 128;
          sum += sample * sample;
        }

        const rms = Math.sqrt(sum / data.length);

        const alpha = rms > smoothedLevel ? attack : release;

        smoothedLevel += (rms - smoothedLevel) * alpha;

        const now = performance.now();

        if (smoothedLevel >= threshold) {
          lastAboveThresholdAt = now;
        }

        const active = smoothedLevel >= threshold || now - lastAboveThresholdAt < holdMs;

        const range = Math.max(0.0001, peak - threshold);

        const intensity = active
          ? Math.min(1, Math.max(0, (smoothedLevel - threshold) / range))
          : 0;

        applyFrame({
          level: smoothedLevel,
          intensity,
          active,
        });

        raf = requestAnimationFrame(loop);
      };

      raf = requestAnimationFrame(loop);

      const handleEnded = () => {
        detach();
      };

      mediaStreamTrack.addEventListener('ended', handleEnded);

      stopMonitor = () => {
        cancelAnimationFrame(raf);
        mediaStreamTrack.removeEventListener('ended', handleEnded);
        source.disconnect();
        analyser.disconnect();
      };
    };

    const handleTrackMuted = () => {
      clearUi();

      stopMonitor?.();
      stopMonitor = undefined;
    };

    const handleTrackUnmuted = () => {
      const mediaStreamTrack = currentTrack?.mediaStreamTrack as MediaStreamTrack | undefined;

      if (mediaStreamTrack && !currentTrack?.isMuted) {
        startMonitor(mediaStreamTrack);
      }
    };

    const handleTrackRestarted = () => {
      const mediaStreamTrack = currentTrack?.mediaStreamTrack as MediaStreamTrack | undefined;

      if (mediaStreamTrack && !currentTrack?.isMuted) {
        startMonitor(mediaStreamTrack);
      }
    };

    const attach = (track: any) => {
      if (currentTrack === track) {
        return;
      }

      detach();

      currentTrack = track;

      const trackEmitter = track as any;

      if (typeof trackEmitter.on === 'function') {
        trackEmitter.on(TrackEvent.Muted, handleTrackMuted);
        trackEmitter.on(TrackEvent.Unmuted, handleTrackUnmuted);

        if ((TrackEvent as any).Restarted) {
          trackEmitter.on((TrackEvent as any).Restarted, handleTrackRestarted);
        }
      }

      const mediaStreamTrack = track.mediaStreamTrack as MediaStreamTrack | undefined;

      if (mediaStreamTrack && !track.isMuted) {
        startMonitor(mediaStreamTrack);
      }

      cleanupCurrentTrack = () => {
        if (typeof trackEmitter.off === 'function') {
          trackEmitter.off(TrackEvent.Muted, handleTrackMuted);
          trackEmitter.off(TrackEvent.Unmuted, handleTrackUnmuted);

          if ((TrackEvent as any).Restarted) {
            trackEmitter.off((TrackEvent as any).Restarted, handleTrackRestarted);
          }
        }
      };
    };

    const scanExistingPublications = () => {
      const publication = getPublications(participant).find((pub) =>
        isScreenShareAudioPublication(pub)
      );

      const track = publication?.track;

      if (publication && isPublicationSubscribed(publication) && track && track.kind === 'audio') {
        attach(track);
      } else {
        detach();
      }
    };

    const onTrackSubscribed = (track: any, publication: any) => {
      if (isScreenShareAudioPublication(publication) && track?.kind === 'audio') {
        attach(track);
      }
    };

    const onTrackUnsubscribed = (track: any, publication: any) => {
      if (isScreenShareAudioPublication(publication) && currentTrack === track) {
        detach();
      }
    };

    const resumeAudioContext = () => {
      const ctx = getSharedAudioContext();

      if (ctx?.state === 'suspended') {
        void ctx.resume().catch(() => undefined);
      }
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        resumeAudioContext();
      }
    };

    const participantEmitter = participant as any;

    if (typeof participantEmitter.on === 'function') {
      participantEmitter.on(ParticipantEvent.TrackSubscribed, onTrackSubscribed);

      participantEmitter.on(ParticipantEvent.TrackUnsubscribed, onTrackUnsubscribed);
    }

    if (typeof document !== 'undefined') {
      document.addEventListener('pointerdown', resumeAudioContext);
      document.addEventListener('keydown', resumeAudioContext);
      document.addEventListener('visibilitychange', onVisibilityChange);
    }

    scanExistingPublications();

    return () => {
      if (typeof participantEmitter.off === 'function') {
        participantEmitter.off(ParticipantEvent.TrackSubscribed, onTrackSubscribed);

        participantEmitter.off(ParticipantEvent.TrackUnsubscribed, onTrackUnsubscribed);
      }

      if (typeof document !== 'undefined') {
        document.removeEventListener('pointerdown', resumeAudioContext);
        document.removeEventListener('keydown', resumeAudioContext);
        document.removeEventListener('visibilitychange', onVisibilityChange);
      }

      detach();
    };
  }, [participant, enabled, speakingClass]);
}
